import { type FC, useMemo } from "react";
import { Dialog } from "@mui/material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { usePostQuery, useUpdatePostMutation } from "@/partner/entities/posts";
import { mapPostToFormValues, type PostFormValues } from "../model";
import { PostForm } from "./PostForm";

interface EditPostDialogProps {
  open: boolean;
  postId: string | null;
  onClose: () => void;
  onCompleted?: () => void;
}

export const EditPostDialog: FC<EditPostDialogProps> = ({
  open,
  postId,
  onClose,
  onCompleted,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const { data, loading } = usePostQuery({
    variables: { id: String(postId) },
    skip: !postId || !open,
  });

  const [updatePost, { loading: updating }] = useUpdatePostMutation({
    onCompleted: () => {
      enqueueSnackbar(t("posts.edit.success"), { variant: "success" });
      onCompleted?.();
    },
    onError: (error) => {
      enqueueSnackbar(error.message, { variant: "error" });
    },
    refetchQueries: ["PostsList"],
  });

  const initialValues = useMemo(
    () => (data ? mapPostToFormValues(data.post) : undefined),
    [data]
  );

  const handleSubmit = async (values: PostFormValues) => {
    await updatePost({
      variables: {
        id: String(postId),
        input: values,
      },
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <PostForm
        title={t("posts.edit.title")}
        loading={loading || updating}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        onClose={onClose}
        metadata={{ url: data?.post?.url }}
      />
    </Dialog>
  );
};
